import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Modal, Pagination, Spinner, StatCard } from '../components/ui';
import { usePagination } from '../lib/usePagination';

type Any = Record<string, any>;

const FORMATS = ['CURIOSITY', 'MYTH_FACT', 'THREE_STEPS', 'COMPARISON', 'NUMBERS', 'DIAGRAM', 'CAROUSEL', 'QUIZ', 'SPACE', 'TERM_OF_DAY'];
const STATUS_TONE: Record<string, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral', GENERATING: 'info', VALIDATING: 'info', READY: 'success', SCHEDULED: 'info',
  PUBLISHING: 'warning', PUBLISHED: 'success', FAILED: 'danger', NEEDS_REVIEW: 'warning', EXPIRED: 'neutral',
};

function fmt(value: unknown): string {
  if (!value) return '—';
  const time = Date.parse(String(value));
  if (!Number.isFinite(time)) return String(value).slice(0, 40);
  return new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' }).format(new Date(time));
}

function svgUrl(post: Any, size = 'portrait'): string | null {
  const id = post?.svgAssets?.[size];
  return typeof id === 'string' && id ? `/api/media/${encodeURIComponent(id)}/file` : null;
}

async function rasterize(url: string, width: number, height: number): Promise<Blob> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Nie udało się wczytać SVG do eksportu PNG'));
    image.src = url;
  });
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D jest niedostępny w tej przeglądarce');
  context.drawImage(image, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Nie udało się utworzyć PNG')), 'image/png'));
}

export function NieboGuardVisualStudioSection() {
  const [posts, setPosts] = useState<Any[]>([]);
  const paged = usePagination(posts, 10);
  const [topics, setTopics] = useState<Any[]>([]);
  const [articles, setArticles] = useState<Any[]>([]);
  const [settings, setSettings] = useState<Any | null>(null);
  const [topicId, setTopicId] = useState('');
  const [articleId, setArticleId] = useState('');
  const [format, setFormat] = useState('CURIOSITY');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<Any | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const [postResult, topicResult, articleResult, settingsResult] = await Promise.all([
      api.nieboguard.visual.list({ limit: 100 }),
      api.nieboguard.topics(),
      api.nieboguard.content.articles().catch(() => ({ items: [] })),
      api.nieboguard.visual.settings(),
    ]);
    setPosts(postResult.posts ?? []);
    setTopics((topicResult.topics ?? []).filter((topic: Any) => topic.status === 'READY'));
    setArticles((articleResult.items ?? []).filter((article: Any) => ['APPROVED', 'PUBLISHED'].includes(article.status)));
    setSettings(settingsResult.settings ?? null);
  }, []);

  useEffect(() => { load().catch(setError); }, [load]);

  const act = async (key: string, action: () => Promise<Any>, success: string) => {
    setBusy(key); setError(null); setMessage('');
    try {
      const result = await action();
      if (result?.scheduledAt) setMessage(`${success} Termin: ${fmt(result.scheduledAt)} (Europe/Warsaw).`);
      else setMessage(success);
      await load();
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  const exportPng = async (post: Any): Promise<string> => {
    if (post.rasterAssetId) return post.rasterAssetId as string;
    const url = svgUrl(post, 'portrait');
    if (!url) throw new Error('Brak szablonu SVG dla tej grafiki');
    const blob = await rasterize(url, 1080, 1350);
    const result = await api.nieboguard.visual.uploadRaster(post.id, blob);
    return String(result.asset?.id ?? result.post?.rasterAssetId ?? '');
  };

  const exportAndQueue = (post: Any) => act(`queue-${post.id}`, async () => {
    await exportPng(post);
    return api.nieboguard.visual.queue(post.id);
  }, 'Grafika wyeksportowana do PNG i dodana do wspólnej kolejki publikacji.');

  const statusCounts = posts.reduce((acc: Record<string, number>, post: Any) => {
    acc[post.status] = (acc[post.status] ?? 0) + 1;
    return acc;
  }, {});

  return <div className="nb-list">
    {error ? <ErrorAlert error={error} /> : null}
    {message ? <div className="alert alert--success">{message}</div> : null}

    <div className="nb-hero">
      <div className="nb-row nb-row--between">
        <div>
          <h2>Visual Studio — grafiki edukacyjne NieboGuard</h2>
          <p>Renderer szablonowy SVG (bez kosztu AI) tworzy trzy rozmiary: 1080×1080, 1080×1350 i 1080×1920. PNG jest eksportowany w przeglądarce i dopiero po nim grafika trafia do wspólnego schedulera. Nic nie publikuje się automatycznie bez zatwierdzenia.</p>
        </div>
        <Badge tone={settings?.enabled ? 'success' : 'warning'}>{settings?.enabled ? 'Moduł aktywny' : 'Moduł wyłączony'}</Badge>
      </div>
      <div className="nb-row">
        <Badge tone="info">Minimum 90 min odstępu</Badge>
        <Badge tone="neutral">Evergreen 80% / aktualności 20%</Badge>
        <Badge tone="warning">TikTok tylko przez zgodną integrację</Badge>
        <Badge tone="success">Wspólny scheduler wideo + grafiki</Badge>
      </div>
    </div>

    <div className="nb-grid nb-grid--stats">
      <StatCard label="Gotowe grafiki" value={statusCounts.READY ?? 0} />
      <StatCard label="Do review" value={statusCounts.NEEDS_REVIEW ?? 0} />
      <StatCard label="Zaplanowane" value={statusCounts.SCHEDULED ?? 0} />
      <StatCard label="Opublikowane" value={statusCounts.PUBLISHED ?? 0} />
    </div>

    <Card title="Utwórz grafikę" actions={<div className="btn-row"><Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => act('plan', () => api.nieboguard.visual.plan(10), 'Utworzono szkice grafik z zatwierdzonych tematów.')}>Zaplanuj 10 grafik</Button><Button size="sm" variant="primary" disabled={busy !== null} onClick={() => act('plan-queue', () => api.nieboguard.visual.planAndQueue(7), 'Utworzono i zakolejkowano grafikę na 7 dni (PNG renderowany serwerowo).')}>{busy === 'plan-queue' ? 'Planuję…' : 'Zaplanuj 7 dni i kolejkuj'}</Button></div>}>
      <div className="nb-grid nb-grid--2">
        <div className="nb-item">
          <div className="nb-item__title">Z tematu NieboGuard</div>
          <label className="field"><span>Temat (READY)</span><select value={topicId} onChange={(event) => setTopicId(event.target.value)}><option value="">Wybierz temat</option>{topics.map((topic) => <option key={topic.id} value={topic.id}>{topic.title}</option>)}</select></label>
          <label className="field"><span>Format</span><select value={format} onChange={(event) => setFormat(event.target.value)}>{FORMATS.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <Button size="sm" variant="primary" disabled={busy !== null || !topicId} onClick={() => act('from-topic', () => api.nieboguard.visual.fromTopic({ topicId, format }), 'Grafika utworzona jako szablon SVG.')}>{busy === 'from-topic' ? 'Tworzę…' : 'Utwórz z tematu'}</Button>
        </div>
        <div className="nb-item">
          <div className="nb-item__title">Z artykułu („Utwórz grafikę z artykułu”)</div>
          <label className="field"><span>Artykuł APPROVED/PUBLISHED</span><select value={articleId} onChange={(event) => setArticleId(event.target.value)}><option value="">Wybierz artykuł</option>{articles.map((article) => <option key={article.id} value={article.id}>{article.title}</option>)}</select></label>
          <Button size="sm" variant="primary" disabled={busy !== null || !articleId} onClick={() => act('from-article', () => api.nieboguard.visual.fromArticle({ articleId, format }), 'Grafika z artykułu utworzona jako szablon SVG.')}>{busy === 'from-article' ? 'Tworzę…' : 'Utwórz z artykułu'}</Button>
          <p className="nb-stat-note">Grafika nie kopiuje cudzych zdjęć. To deterministyczny szablon edukacyjny z źródłem i oznaczeniem „ilustracja edukacyjna”.</p>
        </div>
      </div>
    </Card>

    <Card title="Grafiki" actions={<Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => act('sync', () => api.nieboguard.visual.sync(), 'Statusy publikacji odświeżone.')}>Odśwież statusy</Button>}>
      {!posts.length ? <EmptyState title="Brak grafik" hint="Utwórz grafikę z tematu, artykułu albo zaplanuj partię 10 szablonów." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Podgląd</th><th>Tytuł / format</th><th>Status</th><th>Platformy</th><th>Termin</th><th /></tr></thead>
          <tbody>{paged.pageRows.map((post) => <tr key={post.id}>
            <td>{svgUrl(post) ? <button type="button" onClick={() => setPreview(post)} style={{ padding: 0, border: 0, background: 'none', cursor: 'zoom-in' }}><img src={svgUrl(post)!} alt={post.altText || post.headline} loading="lazy" style={{ width: 92, height: 115, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} /></button> : <span className="nb-muted">—</span>}</td>
            <td><strong>{post.headline}</strong><div className="nb-item__meta">{post.format} · {post.facts?.length ?? 0} faktów · {post.rasterAssetId ? 'PNG gotowy' : 'tylko SVG'}</div></td>
            <td><Badge tone={STATUS_TONE[post.status] ?? 'neutral'}>{post.status}</Badge>{post.error ? <div className="nb-item__meta nb-danger">{post.error}</div> : null}</td>
            <td><div className="nb-item__meta">{(post.metadata?.platforms ?? []).join(', ') || '—'}</div>{(post.metadata?.manualExport ?? []).length ? <div className="nb-item__meta nb-danger">Ręcznie: {(post.metadata.manualExport as string[]).join(', ')}</div> : null}</td>
            <td>{fmt(post.scheduledAt)}</td>
            <td><div className="btn-row">
              <Button size="sm" variant="ghost" onClick={() => setPreview(post)}>Podgląd</Button>
              <Button size="sm" disabled={busy !== null || post.status === 'SCHEDULED' || post.status === 'PUBLISHED'} onClick={() => act(`approve-${post.id}`, () => api.nieboguard.visual.approve(post.id), 'Grafika zatwierdzona.')}>Zatwierdź</Button>
              <Button size="sm" disabled={busy !== null} onClick={() => act(`regen-${post.id}`, () => api.nieboguard.visual.regenerate(post.id), 'Szablon wygenerowany ponownie.')}>Regeneruj</Button>
              <Button size="sm" disabled={busy !== null || post.status === 'SCHEDULED' || post.status === 'PUBLISHED'} onClick={() => act(`raster-${post.id}`, () => api.nieboguard.visual.rasterize(post.id), 'PNG wyrenderowany serwerowo.')}>{busy === `raster-${post.id}` ? 'Renderuję…' : 'Rasteryzuj PNG'}</Button>
              <Button size="sm" variant="primary" disabled={busy !== null || post.status === 'SCHEDULED' || post.status === 'PUBLISHED'} onClick={() => act(`queue-${post.id}`, () => api.nieboguard.visual.queue(post.id), 'Grafika dodana do kolejki (PNG automatycznie).')}>{busy === `queue-${post.id}` ? 'Kolejkuję…' : 'Kolejka (auto PNG)'}</Button>
              {['SCHEDULED', 'READY', 'NEEDS_REVIEW'].includes(post.status) ? <Button size="sm" variant="danger" disabled={busy !== null} onClick={() => act(`cancel-${post.id}`, () => api.nieboguard.visual.cancel(post.id, 'Anulowano przez operatora'), 'Grafika anulowana.')}>Anuluj</Button> : null}
            </div></td>
          </tr>)}</tbody>
        </table><Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onChange={paged.setPage} /></div>}
    </Card>

    <Card title="Ustawienia grafiki i kosztów">
      {!settings ? <Spinner label="Ładowanie ustawień…" /> : <div className="nb-grid nb-grid--2">
        <div className="nb-item"><div className="nb-item__title">Scheduler</div><p className="nb-stat-note">Minimalny odstęp: {settings.minGapMinutes} min · sloty: {settings.slots?.join(', ')} · strefa: Europe/Warsaw. Publikacje wideo i grafiki korzystają z jednego kalendarza.</p><p className="nb-stat-note">Cele dzienne: {(settings.goals ?? []).map((goal: Any) => `${goal.platform} ${goal.perDay}`).join(' · ')}. TikTok jest pomijany, jeśli brak zgodnej integracji — wtedy trafia do ręcznego eksportu.</p></div>
        <div className="nb-item"><div className="nb-item__title">Koszty AI</div><p className="nb-stat-note">Obecny tryb: renderer szablonowy SVG, koszt AI 0 USD. Ilustracje AI są opcjonalne i nie są uruchamiane bez osobnej decyzji. Budżet miesięczny: {settings.monthlyBudgetUsd} USD.</p><p className="nb-stat-note">Cache i deduplikacja: ta sama grafika z tego samego tematu nie jest tworzona ponownie przez 30 dni.</p></div>
      </div>}
    </Card>

    {preview ? <Modal open title={preview.headline} onClose={() => setPreview(null)} wide footer={<><Button onClick={() => setPreview(null)}>Zamknij</Button><Button variant="primary" disabled={busy !== null} onClick={() => { setPreview(null); exportAndQueue(preview).catch(setError); }}>Eksport PNG + kolejka</Button></>}>
      <div className="nb-list">
        <div className="nb-row"><Badge tone={STATUS_TONE[preview.status] ?? 'neutral'}>{preview.status}</Badge><Badge tone="info">{preview.format}</Badge><span className="nb-item__meta">{preview.sources?.length ?? 0} źródeł · {preview.rasterAssetId ? 'PNG gotowy' : 'tylko SVG'}</span></div>
        {svgUrl(preview) ? <img src={svgUrl(preview)!} alt={preview.altText || preview.headline} style={{ width: 'min(100%, 540px)', height: 'auto', borderRadius: 12, border: '1px solid var(--border)' }} /> : null}
        <p style={{ lineHeight: 1.55 }}>{preview.keyMessage}</p>
        <ol className="nb-steps">{(preview.facts ?? []).map((fact: string, index: number) => <li key={index}>{fact}</li>)}</ol>
        <div className="nb-item"><div className="nb-item__title">Opisy platformowe</div>{Object.entries(preview.platformCopy ?? {}).map(([platform, copy]) => <div key={platform} className="nb-stat-note"><strong>{platform}:</strong> {String(copy).slice(0, 400)}</div>)}</div>
        <div className="nb-item"><div className="nb-item__title">Źródła</div>{(preview.sources ?? []).map((source: Any, index: number) => <div key={index}><a href={source.url} target="_blank" rel="noreferrer">{source.name}</a></div>)}</div>
      </div>
    </Modal> : null}
  </div>;
}
